/**
 * ConfigureDeploymentModal — the single "Download Model" / "Push Model" /
 * "Configure deployment" dialog (Figma 5VfM8hXqQfFP4oLdInY8mk 9952:24284,
 * 10010:29514, 10010:29943).
 *
 * One Spark modal owns the whole flow: artifact format (raw files ZIP, Docker
 * image, or a configured registry push target), the task/version rows,
 * infrastructure, file name + instance count, long-input and prediction
 * strategy cards, and the SaaS download quote in the footer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertBanner, ChoiceCard, Input, Modal, SparkModalPortal, Spinner, Tabs, Text } from '@spark';
import { CostEstimateLine } from '@/features/billing/components/CostEstimateFooter';
import {
  downloadButtonLabel,
  downloadQuoteFooterText,
  useDownloadQuote,
} from '@/features/billing/hooks/useDownloadQuote';
import { data_from_server_post } from '@/shared/http/client';
import { isNerTaskType, isVllmTaskType } from '@/features/trainings/constants';
import {
  DEPLOYMENT_INFRA_CATALOG,
  USE_DB_DEPLOYMENT_INFRA_CATALOG,
  resolveDeploymentInfraCatalog,
} from './configureDeployment/deploymentInfraCatalog';
import {
  NO_PUSH_TARGETS_MESSAGE,
  PUSH_ARTIFACT_LABELS,
  configuredPushTypes,
  formatCardsForMode,
  isPushArtifactType,
} from './configureDeployment/downloadFormats';
import {
  filterEligibleTrainings,
  findLatestTrainingForUsecase,
  findTrainingForUsecaseVersion,
  taskDisplayName,
} from './configureDeployment/eligibleTrainings';
import { buildTaskKey } from './configureDeployment/taskKeys';
import { createSelectedTask, createTaskRuntimeConfig } from './configureDeployment/taskRuntimeDefaults';
import {
  buildDefaultDockerImageName,
  buildDockerOutputFilename,
  normalizeDockerImageName,
  validateDockerImageName,
} from './configureDeployment/dockerImageName';
import {
  REGISTRY_TARGETS,
  isDockerHubCloudBlocked,
  registryTargetsFor,
} from './configureDeployment/registryTargets';
import InfrastructureSection, {
  findInstanceMeta,
  pickFirstSelection,
} from './configureDeployment/InfrastructureSection';
import RegistryTargetSection from './configureDeployment/RegistryTargetSection';
import TaskSelectionRows from './configureDeployment/TaskSelectionRows';
import TaskRuntimeConfigPanel from './configureDeployment/TaskRuntimeConfigPanel';
import { FieldError, HelpLabel, InstancesField, SectionTitle } from './configureDeployment/fields';
import './ConfigureDeploymentModal.css';

const CONFIG_NOTE = 'Configuration changes apply to both deployment and download packages.';
const PREDICTION_MISSING_ERROR = 'Select a Prediction strategy for every task to continue.';
const DOCKERHUB_CLOUD_ERROR =
  'Docker Hub push is not available for GCP/Azure in this version. Select AWS.';
const MODAL_WIDTH = 820;

function sumInstanceGroupCount(selectedTasks) {
  return selectedTasks.reduce((total, entry) => {
    if (isVllmTaskType(entry.training?.task_type)) return total;
    return total + (entry.runtimeConfig?.instance_group_count ?? 0);
  }, 0);
}

function taskNeedsPredictionStrategy(entry) {
  return (
    !isVllmTaskType(entry.training?.task_type) &&
    !isNerTaskType(entry.training?.task_type) &&
    !entry.runtimeConfig?.prediction_strategy
  );
}

function defaultArtifactType(mode, formatMode, initialArtifactType, settings) {
  if (mode !== 'download') return 'docker';
  if (initialArtifactType) return initialArtifactType;
  if (formatMode === 'push') return configuredPushTypes(settings)[0] || null;
  return 'zip';
}

export default function ConfigureDeploymentModal({
  open,
  mode,
  formatMode = 'all',
  training,
  initialArtifactType = null,
  availableTrainings = [],
  allUsecases = [],
  instancesCapErrorByTaskKey = {},
  focusedTaskKey = null,
  onClearInstancesCapErrorForTask,
  onFocusedTaskKeyChange,
  onCancel,
  onConfirm,
}) {
  const [catalog, setCatalog] = useState(DEPLOYMENT_INFRA_CATALOG);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [activeTaskKey, setActiveTaskKey] = useState(null);
  const [localCapErrorByTaskKey, setLocalCapErrorByTaskKey] = useState({});
  const [dockerImageName, setDockerImageName] = useState('');
  const [dockerImageNameTouched, setDockerImageNameTouched] = useState(false);
  const [dockerImageNameError, setDockerImageNameError] = useState(null);
  // Set when Done is clicked with a Triton task missing a Prediction
  // strategy; cleared as soon as every task has one again.
  const [predictionStrategyMissing, setPredictionStrategyMissing] = useState(false);

  const [cloudProvider, setCloudProvider] = useState(null);
  const [hardwareDevice, setHardwareDevice] = useState(null);
  const [accelerator, setAccelerator] = useState(null);
  const [instanceType, setInstanceType] = useState(null);
  const [instanceMeta, setInstanceMeta] = useState(null);
  const [preferredInfrastructure, setPreferredInfrastructure] = useState(false);

  // Artifact format + registry push targets. Org settings are fetched once
  // per open (download flows only) and feed both the Format cards and the
  // "Environment" picker of the selected push target.
  const [artifactType, setArtifactType] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [registrySelectedIndex, setRegistrySelectedIndex] = useState(null);
  const [registryTouched, setRegistryTouched] = useState(false);

  const getUsecaseById = useCallback(
    (usecaseId) => allUsecases.find((u) => u._id === usecaseId) || null,
    [allUsecases]
  );

  const primaryTraining = selectedTasks[0]?.training || training;
  const baseModelName = primaryTraining?.base_model_name || null;
  const isVllmFlow = isVllmTaskType(primaryTraining?.task_type);
  const isDownloadMode = mode === 'download';

  const effectiveArtifactType =
    artifactType || defaultArtifactType(mode, formatMode, initialArtifactType, settings);
  const isZipArtifact = effectiveArtifactType === 'zip';
  const isRegistryPushArtifact = isPushArtifactType(effectiveArtifactType);
  const showFormatSection = isDownloadMode;
  // Registry push has no local file, so the name field is replaced by the
  // target section.
  const showNameField = isDownloadMode && !isRegistryPushArtifact;
  const downloadExtension = isZipArtifact ? '.zip' : '.tar';

  const formatCards = useMemo(
    () => formatCardsForMode(formatMode, settings),
    [formatMode, settings]
  );
  const registryTargets = useMemo(
    () => (isRegistryPushArtifact ? registryTargetsFor(effectiveArtifactType, settings) : []),
    [isRegistryPushArtifact, effectiveArtifactType, settings]
  );
  const selectedRegistryTarget =
    isRegistryPushArtifact && registrySelectedIndex != null
      ? registryTargets[registrySelectedIndex] || null
      : null;
  const dockerhubCloudBlocked = isDockerHubCloudBlocked(effectiveArtifactType, cloudProvider);

  // A single Docker image / deployment cannot mix vLLM-served and
  // Triton-served tasks, so the picker only offers the primary task's family.
  const filteredAvailableTrainings = useMemo(
    () => (availableTrainings || []).filter((row) => isVllmTaskType(row?.task_type) === isVllmFlow),
    [availableTrainings, isVllmFlow]
  );
  const eligibleToAdd = useMemo(
    () => filterEligibleTrainings(filteredAvailableTrainings, mode, selectedTasks),
    [filteredAvailableTrainings, mode, selectedTasks]
  );
  const trainingsPool = useMemo(() => {
    const byKey = new Map();
    [...filteredAvailableTrainings, ...selectedTasks.map((entry) => entry.training)].forEach((row) => {
      if (row?.llm_usecase_id != null && row.version != null) byKey.set(buildTaskKey(row), row);
    });
    return Array.from(byKey.values());
  }, [filteredAvailableTrainings, selectedTasks]);

  // SaaS prices model downloads (first ones free, then a flat fee per
  // version): the quote sits in the footer and the Download click is the
  // consent, so no second confirm modal opens.
  const quoteItems = useMemo(
    () =>
      selectedTasks.map(({ training: selected }) => ({
        llm_usecase_id: selected.llm_usecase_id,
        version: selected.version,
      })),
    [selectedTasks]
  );
  const downloadQuote = useDownloadQuote(quoteItems, {
    enabled: open && isDownloadMode && !isRegistryPushArtifact,
  });
  const downloadQuoteText = downloadQuote.quoted
    ? downloadQuoteFooterText(downloadQuote.quote) ??
      (downloadQuote.loading ? 'Checking download cost…' : 'Download cost: unavailable')
    : null;

  const suggestedDockerImageName = useMemo(
    () => buildDefaultDockerImageName(selectedTasks, { hardwareDevice }),
    [selectedTasks, hardwareDevice]
  );

  const hasCapErrorForTask = useCallback(
    (taskKey) => Boolean(localCapErrorByTaskKey[taskKey] || instancesCapErrorByTaskKey[taskKey]),
    [localCapErrorByTaskKey, instancesCapErrorByTaskKey]
  );
  const effectiveActiveTaskKey =
    focusedTaskKey || activeTaskKey || selectedTasks[0]?.taskKey || null;
  const activeEntry =
    selectedTasks.find((entry) => entry.taskKey === effectiveActiveTaskKey) || selectedTasks[0] || null;

  // Infra catalog (live per base model when enabled, static fallback otherwise).
  useEffect(() => {
    if (!open) return undefined;
    if (!USE_DB_DEPLOYMENT_INFRA_CATALOG) {
      setCatalog(DEPLOYMENT_INFRA_CATALOG);
      return undefined;
    }
    let cancelled = false;
    resolveDeploymentInfraCatalog({ baseModelName })
      .then((liveCatalog) => {
        if (!cancelled) setCatalog(liveCatalog);
      })
      .catch(() => {
        if (!cancelled) setCatalog(DEPLOYMENT_INFRA_CATALOG);
      });
    return () => {
      cancelled = true;
    };
  }, [open, baseModelName]);

  // Org settings → configured push targets (download flows only).
  useEffect(() => {
    if (!open || !isDownloadMode) return undefined;
    let cancelled = false;
    setSettings(null);
    setSettingsLoading(true);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => {
        if (cancelled) return;
        setSettings(result || {});
        setSettingsLoading(false);
      },
      () => {
        if (cancelled) return;
        setSettings({});
        setSettingsLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, isDownloadMode]);

  // Reset per-open state and seed the first task + infrastructure.
  useEffect(() => {
    if (!open || !training) return;
    const usecase = getUsecaseById(training.llm_usecase_id);
    const seedTask = createSelectedTask(training, usecase);
    setSelectedTasks([seedTask]);
    setActiveTaskKey(seedTask.taskKey);
    setLocalCapErrorByTaskKey({});
    setDockerImageNameTouched(false);
    setDockerImageNameError(null);
    setPredictionStrategyMissing(false);
    setArtifactType(null);
    setRegistrySelectedIndex(null);
    setRegistryTouched(false);

    const preferredConfig =
      usecase?.preferred_deployment_config || training?.usecase_preferred_deployment_config || null;
    const seedInfra = preferredConfig
      ? {
          cloud: preferredConfig.cloud_provider,
          hardware: preferredConfig.hardware_device,
          accelerator: preferredConfig.accelerator,
          instance: preferredConfig.instance_type
            ? { value: preferredConfig.instance_type, label: preferredConfig.instance_type }
            : null,
        }
      : pickFirstSelection(catalog);
    if (seedInfra) {
      setCloudProvider(seedInfra.cloud);
      setHardwareDevice(seedInfra.hardware);
      setAccelerator(seedInfra.accelerator);
      setInstanceType(seedInfra.instance?.value || null);
      setInstanceMeta(
        seedInfra.instance ||
          findInstanceMeta(
            catalog,
            seedInfra.cloud,
            seedInfra.hardware,
            seedInfra.accelerator,
            seedInfra.instance?.value
          )
      );
    }
    setPreferredInfrastructure(Boolean(preferredConfig));
  }, [open, training, catalog, getUsecaseById]);

  // Suggested file name follows the selection until the operator edits it.
  useEffect(() => {
    if (!open || !isDownloadMode || dockerImageNameTouched) return;
    setDockerImageName(suggestedDockerImageName);
    setDockerImageNameError(null);
  }, [open, isDownloadMode, suggestedDockerImageName, dockerImageNameTouched]);

  // Pre-select the only configured target of the chosen registry.
  useEffect(() => {
    setRegistrySelectedIndex(registryTargets.length === 1 ? 0 : null);
    setRegistryTouched(false);
  }, [effectiveArtifactType, registryTargets.length]);

  useEffect(() => {
    if (focusedTaskKey) setActiveTaskKey(focusedTaskKey);
  }, [focusedTaskKey]);

  // Also clear the footer error when the offending task is removed.
  useEffect(() => {
    if (predictionStrategyMissing && !selectedTasks.some(taskNeedsPredictionStrategy)) {
      setPredictionStrategyMissing(false);
    }
  }, [selectedTasks, predictionStrategyMissing]);

  const maxInstances = instanceMeta?.maxInstances ?? null;
  const totalInstanceCount = useMemo(() => sumInstanceGroupCount(selectedTasks), [selectedTasks]);

  const clearCapErrorForTask = useCallback(
    (taskKey) => {
      setLocalCapErrorByTaskKey((prev) => {
        if (!prev[taskKey]) return prev;
        const next = { ...prev };
        delete next[taskKey];
        return next;
      });
      onClearInstancesCapErrorForTask?.(taskKey);
    },
    [onClearInstancesCapErrorForTask]
  );

  const clearAllCapErrors = useCallback(() => {
    setLocalCapErrorByTaskKey({});
    selectedTasks.forEach((entry) => onClearInstancesCapErrorForTask?.(entry.taskKey));
  }, [selectedTasks, onClearInstancesCapErrorForTask]);

  const handleInfrastructureChange = (next) => {
    setCloudProvider(next.cloudProvider);
    setHardwareDevice(next.hardwareDevice);
    setAccelerator(next.accelerator);
    setInstanceType(next.instanceType);
    setInstanceMeta(next.instanceMeta);
    clearAllCapErrors();
  };

  const handleAddTask = () => {
    const row = eligibleToAdd[0];
    if (!row) return;
    const entry = createSelectedTask(row, getUsecaseById(row.llm_usecase_id));
    setSelectedTasks((prev) => [...prev, entry]);
    setActiveTaskKey(entry.taskKey);
    onFocusedTaskKeyChange?.(null);
  };

  const replaceTask = (taskKey, nextEntry) => {
    setSelectedTasks((prev) => prev.map((entry) => (entry.taskKey === taskKey ? nextEntry : entry)));
    clearCapErrorForTask(taskKey);
    if (effectiveActiveTaskKey === taskKey) setActiveTaskKey(nextEntry.taskKey);
  };

  const handleTaskSelect = (taskKey, usecaseId) => {
    const current = selectedTasks.find((entry) => entry.taskKey === taskKey);
    if (!current || current.training.llm_usecase_id === usecaseId) return;
    const nextTraining = findLatestTrainingForUsecase(trainingsPool, mode, usecaseId);
    if (!nextTraining) return;
    replaceTask(taskKey, createSelectedTask(nextTraining, getUsecaseById(usecaseId)));
  };

  const handleVersionSelect = (taskKey, version, usecaseId) => {
    const nextTraining = findTrainingForUsecaseVersion(trainingsPool, usecaseId, version);
    if (!nextTraining) return;
    const usecase = getUsecaseById(usecaseId);
    const prior = selectedTasks.find((entry) => entry.taskKey === taskKey);
    replaceTask(taskKey, {
      ...createSelectedTask(nextTraining, usecase),
      runtimeConfig: prior?.runtimeConfig || createTaskRuntimeConfig(nextTraining, usecase),
    });
  };

  const handleRemoveTask = (taskKey) => {
    if (selectedTasks.length <= 1) return;
    const remaining = selectedTasks.filter((t) => t.taskKey !== taskKey);
    setSelectedTasks(remaining);
    clearCapErrorForTask(taskKey);
    if (effectiveActiveTaskKey === taskKey) {
      setActiveTaskKey(remaining[0]?.taskKey || null);
      onFocusedTaskKeyChange?.(null);
    }
  };

  const updateTaskRuntimeConfig = useCallback((taskKey, runtimeConfig) => {
    setSelectedTasks((prev) =>
      prev.map((entry) => (entry.taskKey === taskKey ? { ...entry, runtimeConfig } : entry))
    );
    if (runtimeConfig?.prediction_strategy) setPredictionStrategyMissing(false);
  }, []);

  const getCapErrorMessage = (taskKey) => {
    const external = instancesCapErrorByTaskKey[taskKey];
    if (typeof external === 'string' && external) return external;
    if (
      localCapErrorByTaskKey[taskKey] &&
      typeof maxInstances === 'number' &&
      totalInstanceCount > maxInstances &&
      instanceType
    ) {
      return `Up to ${maxInstances} instances on ${instanceType}`;
    }
    return null;
  };

  const handleFormatSelect = (value) => {
    if (value === effectiveArtifactType) return;
    setArtifactType(value);
    setDockerImageNameError(null);
  };

  const dockerNameValidationError = showNameField
    ? validateDockerImageName(dockerImageName, downloadExtension)
    : null;
  const registryTargetMissing = isRegistryPushArtifact && !selectedRegistryTarget;
  const pushTargetsUnavailable =
    isDownloadMode && formatMode === 'push' && !settingsLoading && !formatCards.length;

  const isValid =
    selectedTasks.length > 0 &&
    !!cloudProvider &&
    !!instanceType &&
    !!effectiveArtifactType &&
    selectedTasks.every((entry) => entry.runtimeConfig.instance_group_count >= 1) &&
    !dockerNameValidationError &&
    !registryTargetMissing &&
    !dockerhubCloudBlocked &&
    !pushTargetsUnavailable;

  const handleConfirm = async () => {
    if (submitting) return;
    if (showNameField) {
      const nameError = validateDockerImageName(dockerImageName, downloadExtension);
      if (nameError) {
        setDockerImageNameError(nameError);
        return;
      }
    }
    if (registryTargetMissing) {
      setRegistryTouched(true);
      return;
    }
    if (dockerhubCloudBlocked) return;
    // Classification tasks must carry a Prediction strategy; vLLM and NER
    // tasks legitimately have none. Surfacing this on click (instead of
    // disabling the button) tells the operator why nothing happened.
    if (selectedTasks.some(taskNeedsPredictionStrategy)) {
      setPredictionStrategyMissing(true);
      return;
    }
    if (!isValid) return;

    const overCap = typeof maxInstances === 'number' && totalInstanceCount > maxInstances;
    if (overCap && instanceType) {
      setLocalCapErrorByTaskKey(Object.fromEntries(selectedTasks.map((entry) => [entry.taskKey, true])));
      setActiveTaskKey(selectedTasks[0].taskKey);
      onFocusedTaskKeyChange?.(selectedTasks[0].taskKey);
      return;
    }

    const normalizedDockerName = normalizeDockerImageName(dockerImageName, downloadExtension);
    const downloadOutputFilename = showNameField
      ? buildDockerOutputFilename(
          normalizedDockerName,
          selectedTasks[0].training,
          selectedTasks[0].runtimeConfig,
          downloadExtension
        )
      : null;
    const registryConfig = selectedRegistryTarget
      ? REGISTRY_TARGETS[effectiveArtifactType].sharedConfig(selectedRegistryTarget)
      : null;

    setSubmitting(true);
    try {
      await onConfirm({
        mode,
        artifactType: effectiveArtifactType,
        sharedConfig: {
          cloud_provider: cloudProvider,
          hardware_device: hardwareDevice,
          accelerator: accelerator || null,
          instance_type: instanceType,
          device: (hardwareDevice || '').toLowerCase() === 'cpu' ? 'cpu' : 'gpu',
          preferred_infrastructure: preferredInfrastructure,
          ...(registryConfig || {}),
          ...(downloadOutputFilename ? { output_filename: downloadOutputFilename } : {}),
        },
        dockerImageName: showNameField ? normalizedDockerName : null,
        tasks: selectedTasks.map((entry) => ({
          training: entry.training,
          config: { ...entry.runtimeConfig },
        })),
      });
    } finally {
      // The parent closes the modal on success; on validation errors
      // (instances cap, duplicate push) it stays open, so always drop the spinner.
      setSubmitting(false);
    }
  };

  const renderRuntimePanel = (entry, { showInstances }) => (
    <TaskRuntimeConfigPanel
      key={entry.taskKey}
      training={entry.training}
      runtimeConfig={entry.runtimeConfig}
      onRuntimeConfigChange={(runtimeConfig) => updateTaskRuntimeConfig(entry.taskKey, runtimeConfig)}
      instancesCapErrorMessage={getCapErrorMessage(entry.taskKey)}
      onInstancesFieldChange={clearAllCapErrors}
      showInstances={showInstances}
    />
  );

  const singleTriton = selectedTasks.length === 1 && !isVllmFlow;
  const fileNameField = showNameField ? (
    <div className="configure-deployment-modal__field configure-deployment-modal__field--grow">
      <Input
        label={
          <HelpLabel
            text="File Name"
            tooltip={
              isZipArtifact
                ? 'File name used when you save the raw model files (.zip) to your computer. The suggested name includes _cpu or _gpu so it matches your infrastructure selection.'
                : 'File name used when you save the Docker image to your computer. The suggested name includes _cpu or _gpu so it matches your infrastructure selection.'
            }
          />
        }
        value={dockerImageName}
        placeholder={suggestedDockerImageName}
        danger={Boolean(dockerImageNameError)}
        showHelperText
        helperText={
          dockerImageNameError ||
          `Saved as ${normalizeDockerImageName(dockerImageName, downloadExtension) || suggestedDockerImageName}${downloadExtension}${
            selectedTasks.length > 1
              ? isZipArtifact
                ? ' (one ZIP with all selected models)'
                : ' (one image with all selected models)'
              : ''
          }`
        }
        onChange={(event) => {
          setDockerImageNameTouched(true);
          setDockerImageName(event.target.value);
          if (dockerImageNameError) {
            setDockerImageNameError(validateDockerImageName(event.target.value, downloadExtension));
          }
        }}
        onBlur={() => setDockerImageNameError(validateDockerImageName(dockerImageName, downloadExtension))}
      />
    </div>
  ) : null;

  const title = !isDownloadMode ? 'Configure deployment' : formatMode === 'push' ? 'Push Model' : 'Download Model';
  const primaryLabel = isRegistryPushArtifact
    ? PUSH_ARTIFACT_LABELS[effectiveArtifactType]
    : isDownloadMode
      ? downloadQuote.quoted
        ? downloadButtonLabel(downloadQuote.quote)
        : 'Download'
      : 'Done';

  const footerSlot = (
    <div className="configure-deployment-modal__footer-note-wrap">
      {downloadQuoteText ? (
        <CostEstimateLine
          className="configure-deployment-modal__footer-note"
          icon="dollar"
          text={downloadQuoteText}
          loading={downloadQuote.loading}
        />
      ) : (
        <Text variant="base-regular" color="secondary" className="configure-deployment-modal__footer-note">
          {CONFIG_NOTE}
        </Text>
      )}
      {predictionStrategyMissing ? <FieldError>{PREDICTION_MISSING_ERROR}</FieldError> : null}
    </div>
  );

  return (
    <SparkModalPortal open={open} onClose={onCancel} size="slot" width={MODAL_WIDTH} destroyOnClose>
      <Modal
        className="configure-deployment-modal"
        title={title}
        subtitle="Select the tasks to include, then choose how each task should run."
        showSubtitle={!isDownloadMode}
        showFooterSlot
        footerSlot={footerSlot}
        tertiaryLabel="Cancel"
        tertiaryDisabled={submitting}
        primaryLabel={primaryLabel}
        primaryDisabled={!isValid}
        primaryLoading={submitting}
        primaryDisableWhenReadOnly={!isDownloadMode || isRegistryPushArtifact}
        fixedHeight={false}
        onCloseClick={onCancel}
        onTertiaryClick={onCancel}
        onPrimaryClick={handleConfirm}
      >
        <div className="configure-deployment-modal__body">
          {showFormatSection ? (
            <section className="configure-deployment-modal__section" aria-label="Format">
              <SectionTitle>Format</SectionTitle>
              {settingsLoading && !formatCards.length ? (
                <div className="configure-deployment-modal__format-loading">
                  <Spinner size="small" />
                </div>
              ) : pushTargetsUnavailable ? (
                <AlertBanner
                  feedback="info"
                  text={NO_PUSH_TARGETS_MESSAGE}
                  showButton={false}
                  showCloseButton={false}
                />
              ) : (
                <div className="configure-deployment-modal__card-row" role="group" aria-label="Format">
                  {formatCards.map((card) => (
                    <ChoiceCard
                      key={card.value}
                      size="large"
                      iconSlug={card.iconSlug}
                      label={card.label}
                      selected={effectiveArtifactType === card.value}
                      data-value={card.value}
                      onClick={() => handleFormatSelect(card.value)}
                    />
                  ))}
                </div>
              )}
            </section>
          ) : null}

          <TaskSelectionRows
            selectedTasks={selectedTasks}
            trainingsPool={trainingsPool}
            mode={mode}
            canAddTask={!isVllmFlow}
            addTaskDisabled={!eligibleToAdd.length}
            onAddTask={handleAddTask}
            onTaskSelect={handleTaskSelect}
            onVersionSelect={handleVersionSelect}
            onRemoveTask={handleRemoveTask}
          />

          <InfrastructureSection
            catalog={catalog}
            cloudProvider={cloudProvider}
            hardwareDevice={hardwareDevice}
            accelerator={accelerator}
            instanceType={instanceType}
            onChange={handleInfrastructureChange}
          />

          {isRegistryPushArtifact ? (
            <RegistryTargetSection
              artifactType={effectiveArtifactType}
              targets={registryTargets}
              loading={settingsLoading}
              selectedIndex={registrySelectedIndex}
              onSelect={(index) => {
                setRegistryTouched(true);
                setRegistrySelectedIndex(index);
              }}
              showRequiredError={registryTouched}
              cloudBlockedMessage={dockerhubCloudBlocked ? DOCKERHUB_CLOUD_ERROR : null}
            />
          ) : null}

          {singleTriton && activeEntry ? (
            <>
              <div className="configure-deployment-modal__fields-row configure-deployment-modal__fields-row--name">
                {fileNameField}
                <InstancesField
                  value={activeEntry.runtimeConfig.instance_group_count}
                  onChange={(value) => {
                    clearAllCapErrors();
                    updateTaskRuntimeConfig(activeEntry.taskKey, {
                      ...activeEntry.runtimeConfig,
                      instance_group_count: value,
                    });
                  }}
                  errorMessage={getCapErrorMessage(activeEntry.taskKey)}
                />
              </div>
              {renderRuntimePanel(activeEntry, { showInstances: false })}
            </>
          ) : (
            <>
              {fileNameField ? (
                <div className="configure-deployment-modal__fields-row configure-deployment-modal__fields-row--name">
                  {fileNameField}
                </div>
              ) : null}
              {selectedTasks.length > 1 && activeEntry ? (
                <div className="configure-deployment-modal__task-tabs">
                  <Tabs
                    fill
                    items={selectedTasks.map((entry) => ({
                      id: entry.taskKey,
                      label: (
                        <span className="configure-deployment-modal__tab-label">
                          {taskDisplayName(entry.training)}
                          {hasCapErrorForTask(entry.taskKey) ? (
                            <span className="configure-deployment-modal__tab-error-dot" aria-label="Has error" />
                          ) : null}
                        </span>
                      ),
                    }))}
                    activeId={activeEntry.taskKey}
                    onChange={(id) => {
                      setActiveTaskKey(id);
                      onFocusedTaskKeyChange?.(null);
                    }}
                  />
                  <div className="configure-deployment-modal__tab-panel">
                    {renderRuntimePanel(activeEntry, { showInstances: true })}
                  </div>
                </div>
              ) : activeEntry && !isVllmFlow ? (
                renderRuntimePanel(activeEntry, { showInstances: true })
              ) : null}
            </>
          )}
        </div>
      </Modal>
    </SparkModalPortal>
  );
}
